import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import HaveIBeenPwned from 'services/hibp.service';
import SEO from 'components/shared/SEO';
import ButtonPrimary from 'components/shared/ButtonPrimary';
import ButtonShowPassword from 'components/shared/ButtonShowPassword';
import Field from 'components/shared/Field';
import FormError from 'components/shared/FormError';
import Row from 'components/shared/Row';
import Text from 'components/shared/Text';
import Heading from 'components/shared/Heading';
import ERRORS from 'constants/errors';
import STEPS from 'constants/steps';
import {
  startLoading,
  stopLoading,
  setError,
  clearError,
  resetPasswordReturn
} from 'reduxConfig/auth.creators';
import { someValuesTruthy } from 'helpers/objects';
import { checkPasswordStrength } from 'helpers/passwords';
import styles from './styles.module.scss';

const microapps = window.MICROFRONTENDS_CONFIG;

function ResetPasswordReturn({ dispatch, loading, error: apiError, step, actionCode, isMobile }) {
  const initialState = {
    password: '',
    passwordTwo: ''
  };
  const { t } = useTranslation();
  const [values, setValues] = useState(initialState);
  const [passwordScore, setPasswordScore] = useState(0);
  const [passwordPwned, setPasswordPwned] = useState(null);
  const [errors, setErrors] = useState(initialState);

  const [inputTypes, setInputTypes] = useState({
    password: 'password',
    passwordTwo: 'password'
  });

  const { password, passwordTwo } = values;

  const handlePasswordChange = e => {
    if (apiError) dispatch(clearError());
    setValues({ ...values, password: e.target.value });
    setErrors({
      ...errors,
      password: validatePasswordOne(e.target.value),
      passwordTwo:
        values.passwordTwo.length > 0
          ? validatePasswordTwo(values.passwordTwo, e.target.value)
          : errors.passwordTwo
    });
  };

  const handlePasswordTwoChange = e => {
    if (apiError) dispatch(clearError());
    setValues({ ...values, passwordTwo: e.target.value });
    setErrors({ ...errors, passwordTwo: validatePasswordTwo(e.target.value, values.password) });
  };

  const handleSubmit = e => {
    e.preventDefault();

    const submissionErrors = {
      password: validatePasswordOne(password),
      passwordTwo: validatePasswordTwo(passwordTwo, password)
    };

    if (someValuesTruthy(submissionErrors)) setErrors(submissionErrors);
    else checkIsPasswordPwned(password);
  };

  const checkIsPasswordPwned = value => {
    dispatch(startLoading(true));
    HaveIBeenPwned.isPasswordPwned(value).then(pwned => {
      if (pwned) {
        dispatch(setError(ERRORS.passwordPwned));
        dispatch(stopLoading());
      } else {
        dispatch(resetPasswordReturn(actionCode, password));
      }
    });
  };

  const disableSubmit = () => {
    if (!password || !passwordTwo || passwordPwned !== false || passwordScore < 4) return true;
    if (someValuesTruthy(errors)) return true;
    return false;
  };

  const setPasswordInputType = key => {
    const type = inputTypes[key];
    const newInputType = type === 'text' ? 'password' : 'text';
    setInputTypes({ ...inputTypes, [key]: newInputType });
  };

  const validatePasswordOne = value => {
    if (value.length === 0) {
      return '';
    }
    const { error, score, hibpPromise } = checkPasswordStrength(value, values.email);
    setPasswordScore(score);
    if (hibpPromise) {
      hibpPromise
        .then(isPwned => {
          setPasswordPwned(isPwned);
          if (isPwned) {
            setErrors({ ...errors, password: ERRORS.passwordCompromised });
          }
        })
        .catch(err => {
          console.error('HIBP error', err); // eslint-disable-line
          setPasswordPwned(false);
        });
    }
    return error;
  };

  const validatePasswordTwo = (value, currentPassword) => {
    if (value !== currentPassword) return ERRORS.passwordsNotEqual;
    return null;
  };

  if (step === STEPS.passwordChangeComplete) {
    return (
      <>
        <SEO title={t('auth_action_password_meta_title')} />
        <Heading tag="h1">{t('auth_action_password_success')}</Heading>

        <Text tag="p" muted styles={[styles.belowHeaderText]}>
          {t('auth_action_password_continue')}
        </Text>

        <ButtonPrimary fullWidth={isMobile} to={microapps.routes.login.path}>
          {t('auth_action_continue')}
        </ButtonPrimary>
      </>
    );
  }

  return (
    <>
      <SEO title={t('auth_action_password_meta_title')} />
      <Heading tag="h1">{t('auth_action_password_meta_title')}</Heading>

      <Text tag="p" muted styles={[styles.belowHeaderText]}>
        {t('auth_action_password_prompt')}
      </Text>

      <form onSubmit={handleSubmit}>
        <Field
          id="password"
          name={t('common_password')}
          value={password}
          error={errors.password}
          type={inputTypes.password}
          showPasswordStrength
          passwordStrength={passwordScore}
          passwordCompromised={passwordPwned}
          onBlur={handlePasswordChange}
          onChange={handlePasswordChange}
          inlineElements={<ButtonShowPassword onClick={() => setPasswordInputType('password')} />}
        />

        <Field
          id="passwordTwo"
          name={t('common_repeat_password')}
          value={passwordTwo}
          error={errors.passwordTwo}
          type={inputTypes.passwordTwo}
          onBlur={handlePasswordTwoChange}
          onChange={handlePasswordTwoChange}
          inlineElements={<ButtonShowPassword onClick={() => setPasswordInputType('passwordTwo')} />}
        />

        {apiError && <FormError>{t(apiError)}</FormError>}

        <Row>
          <ButtonPrimary disabled={disableSubmit()} loading={loading} type="submit" fullWidth>
            {t('auth_action_password_button')}
          </ButtonPrimary>
        </Row>
      </form>
    </>
  );
}

ResetPasswordReturn.propTypes = {
  actionCode: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  error: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  step: PropTypes.string,
  isMobile: PropTypes.bool.isRequired
};

ResetPasswordReturn.defaultProps = {
  actionCode: null,
  error: null,
  step: null
};

export default ResetPasswordReturn;
